import { isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { forwardRef, useEffect, useRef, useState } from 'react';
import Flourish from '@lib/flourishPatch';
import { getStatePromise } from 'utils/flourishUtils';
const FlourishKey = process.env.REACT_APP_FLOURISH_API_KEY;

const FlourishChart = forwardRef(
  ({
    visualizationId,
    containerId = `containerId_${uuidv4().replace(/-/g, '_')}`,
    width = '100%',
    height,
    data,
    style,
    withFooter = true,
    onChartLoad: onLoad,
  }) => {
    const flourishChartRef = useRef(null);
    const containerRef = useRef(null);
    const initialDataRef = useRef(data);
    const [isVisible, setIsVisible] = useState(false);
    const [isChartLoaded, setIsChartLoaded] = useState(false);

    // IntersectionObserver: mount chart only when visible on screen
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            setIsVisible(false);
          }
        },
        { threshold: 0.1 },
      );

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }, []);

    const footerState = {
      layout: {
        footer_text_color: '#37434E',
        subtitle_color: '#37434E',
        subtitle_size: 'custom',
        subtitle_size_custom: 0.9,
        subtitle_space_above: '0',
        subtitle_styling: true,
        title_color: '#37434E',
        title_size: 'custom',
        title_size_custom: 0.9,
        title_space_above: '0',
        margin_left: 1,
        margin_right: 1,
        title_styling: true,
        header_border: 'bottom',
        source_name: '',
      },
    };

    const dataState = withFooter
      ? {
          ...(data?.state || {}),
          layout: Object.assign({}, footerState.layout, data?.state?.layout),
        }
      : {
          ...data?.state,
          layout: {
            ...data.state?.layout,
            footer_border: 'none',
            footer_logo_enabled: false,
            source_name: '',
          },
        };

    const opts = {
      api_key: FlourishKey,
      container: `#${containerId}`,
      base_visualisation_id: visualizationId,
      metadata: data?.metadata,
      data: data?.data,
      bindings: data?.bindings,
      state: dataState,
      ...(width && { width }),
      ...(height && { height }),
    };

    // Initialize chart only when visible
    useEffect(() => {
      if (!isVisible) return;

      if (!flourishChartRef.current) {
        flourishChartRef.current = new Flourish.Live(opts);

        const container = document.getElementById(containerId);
        if (!container) return;

        const attachLoadListener = (iframe) => {
          iframe.addEventListener('load', () => {
            setIsChartLoaded(true);
          });
          onLoad?.();
        };

        const existingIframe = container.querySelector('iframe');
        if (existingIframe) {
          attachLoadListener(existingIframe);
          return;
        }

        const observer = new MutationObserver(() => {
          const iframe = container.querySelector('iframe');
          if (iframe) {
            attachLoadListener(iframe);
            observer.disconnect();
          }
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => observer.disconnect();
      }
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update chart when data changes (only if visible)
    useEffect(() => {
      if (!isVisible) return;

      const updateChart = async () => {
        if (flourishChartRef.current && !isEqual(data, initialDataRef.current)) {
          try {
            const currentState = await getStatePromise(flourishChartRef.current);

            flourishChartRef.current.update({
              state: currentState,
              bindings: initialDataRef.current?.bindings,
              data: data?.data,
              metadata: initialDataRef.current?.metadata,
            });

            initialDataRef.current = data;
          } catch (error) {
            console.error('Error while retrieving the state of the chart:', error);
          }
        }
      };

      updateChart();
    }, [data, isVisible]);

    return (
      <div
        ref={containerRef}
        className='flex flex-row items-center justify-center h-full w-full'
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          ...style,
        }}>
        <div
          id={containerId}
          style={{ width: '100%', height: '100%', ...style }}
          className='mr-[-12px] ml-[-12px]'
        />
      </div>
    );
  },
);

FlourishChart.displayName = 'FlourishChart';

export default FlourishChart;
